import { Injectable } from '@angular/core';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { AppsService } from './apps.service';

/**
 * MenuScopeService — the set of routes the signed-in user's APP PROFILE grants.
 *
 * SECURITY (audit obs #2 — Privilege Escalation, CWE-269).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS IS A UI CONTROL. IT IS NOT A SECURITY BOUNDARY.
 * ─────────────────────────────────────────────────────────────────────────────
 * Everything here runs in the browser, on data the user can edit. It decides what
 * the app will NAVIGATE to; it cannot decide what the server will DO. The audited
 * finding was a State user pasting a ULB URL and completing a privileged write —
 * and the write went through the API, which never sees this code. An attacker uses
 * curl and a valid token; Angular is not in that path.
 *
 * The real control is server-side, per route:
 *   checkUser.hasToken                          (authentication)
 *   checkUser.vldSelect/vldInsert/vldUpdate/vldDelete(<object>)   (authorisation)
 * See client/admin/src/app/apps/auth/role-guard.service.ts, which documents the
 * same split, and do not cite this file as the reason anything is safe.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE THE ROUTES COME FROM
 * ─────────────────────────────────────────────────────────────────────────────
 * GET auth2/admin/menu → umCtrl.getAllMnuItmsCtrl → usrmngtmdl.getAllMnuItmsMdl,
 * which resolves the menu from the user's APP PROFILE:
 *
 *     usr_clnt_tnt_rel_t.app_prfle_id
 *       → app_prfle_lst_t → app_prfle_app_rel_t → app_lst_t
 *       → CONCAT('/internal', app_lst_t.app_url_tx)
 *
 * so this uses exactly the same source, and the same endpoint, that renders the
 * side navigation. There is no second list to drift from the first.
 *
 * Measured 2026-08-18, app_prfle_id 9000000 ("Audit Satelevel") — the audited
 * State profile, role 5000000 "Audit". The menu resolves to five routes:
 * urban-dashboard, routeDeviation-dashboard, heatmap, tracking, reports. So
 * /internal/ams_vehicles/add_newvehcile is NOT permitted and the guard refuses it.
 *
 * BUT NOT FOR THE REASON IT LOOKS LIKE, AND THE DIFFERENCE MATTERS.
 * The profile DOES carry app_prfle_app_rel_t rows for app_id 2000007 "Manage
 * Vehicle" and app_id 124 "Vehicles Master". They are excluded because those rows
 * carry dsble_in = 1, and getAllMnuItmsMdl filters on dsble_in = 0 (alongside
 * a.a_in = 1 and a.cmpnt_id = 1). The screen was de-provisioned by HIDING THE MENU
 * ENTRY, not by removing the grant.
 *
 * Two consequences:
 *
 *   1. This guard depends on a flag that means "hide from the sidebar", not
 *      "revoke". Anyone flipping dsble_in back to 0 — a cosmetic-looking change in
 *      an admin screen — silently re-permits the route here, with no review.
 *
 *   2. dsble_in is read by NOTHING on the server. It was standing in for the
 *      permission the API actually checks (usr_rle_objct_rel_t.insrt_in on
 *      'assetTracking'), which role 5000000 still holds. That is the root cause of
 *      the audited finding: measured the same day, the Audit role has
 *      slct_in=1 insrt_in=1 updt_in=1 — so "Add New Device" succeeds via the API
 *      whether or not this guard ever runs.
 *
 * See sql/2026-08-18_obs2_step3_grant_remediation.sql. 306 rows across 42 profiles
 * and 66 screens were de-provisioned this same way, so this is a pattern, not one
 * bad row.
 */
@Injectable({ providedIn: 'root' })
export class MenuScopeService {

  /** Resolved once per session and shared; null until the first successful load. */
  private routes$: Observable<string[]> | null = null;

  /** Last resolved set, for synchronous checks inside the guard. */
  private readonly routesSubject = new BehaviorSubject<string[] | null>(null);

  /**
   * Routes that are ALWAYS permitted, whatever the profile says.
   *
   * Without these the guard can log a user out of the page it just sent them to,
   * which is an infinite loop rather than a security control:
   *   /internal          the login screen itself
   *   /internal/dashboard the fallback every redirect and `homeUrl` points at
   * Error/landing routes are included for the same reason.
   *
   * SELF-SERVICE ACCOUNT SCREENS — /internal/pages/profile and
   * /internal/pages/help-desk-admin — are here for a different reason, and it is
   * not a relaxation of the rule.
   *
   * The rule this guard enforces is "a user may only use the screens their own app
   * profile renders", and that profile is app_prfle_app_rel_t → app_lst_t: the
   * SIDEBAR. These two are not sidebar screens. They are reached from the avatar
   * menu (header.component.ts editprfl(), side-nav.component.ts editprfl()) and
   * belong to every signed-in user by construction, so they carry no app_lst_t row
   * and NO app profile can ever grant them. Without this entry the guard refuses
   * Edit Profile for every user whose menu resolves non-empty — which at
   * menuScope.mode='redirect' is the click silently bouncing back to the dashboard.
   * The empty-menu fail-open covered only the 21% with no app_prfle_id, so this
   * read as an intermittent bug rather than the total one it was.
   *
   * Listing them grants no data. Both screens resolve their subject from the
   * verified token server-side — apiv1/admin/web/user/profile returns the caller's
   * own row, and Help Desk Admin re-resolves hlpdsk_admn_in from the JWT — and
   * /internal/pages/help-desk-admin keeps its own HelpDeskAdminGuard, which runs
   * after this one. Matching here is EXACT equality, not the prefix match used for
   * granted routes (see isPermitted), so the rest of /internal/pages — invoice,
   * members, pricing, setting, blog — stays refused.
   */
  private static readonly ALWAYS_ALLOWED = [
    '/internal',
    '/internal/dashboard',
    '/internal/error',
    '/internal/404',
    '/internal/unauthorised',
    '/internal/pages/profile',
    '/internal/pages/help-desk-admin'
  ];

  constructor(private apiSrv: AppsService) { }

  /**
   * Normalise a route for comparison.
   *
   * app_lst_t.app_url_tx is not clean data. Observed shapes include
   * '/ams_vehicles/vehicles', '#/web/web/alerts/subscribe', '/ds#/web/web/...',
   * bare 'VehiclesPage', and empty strings on group headers. Comparing raw values
   * would silently fail to match and log the user out of a page they are entitled
   * to — the exact failure this must not have.
   */
  static normalise(url: string): string {
    if (!url) { return ''; }
    let u = String(url).trim();
    u = u.split('?')[0].split(';')[0];      // query + Angular matrix params
    const hash = u.indexOf('#');
    if (hash !== -1) { u = u.slice(hash + 1); }   // '#/web/...' and '/ds#/web/...'
    u = u.replace(/\/+$/, '');
    if (u && u.charAt(0) !== '/') { u = '/' + u; }
    return u.toLowerCase();
  }

  /** True when the profile grants no usable routes — see isPermitted(). */
  static isEmpty(routes: string[] | null): boolean {
    return !routes || routes.length === 0;
  }

  /** Load (once) the permitted route set for this session. */
  load(): Observable<string[]> {
    if (!this.routes$) {
      this.routes$ = this.apiSrv.get('auth2/admin/menu').pipe(
        map((res: any) => {
          const rows = (res && res['status'] == 200 && res['data']) ? res['data'] : [];
          return MenuScopeService.flatten(rows);
        }),
        // FAIL OPEN on any transport/parse failure: an empty set means "unknown",
        // and treating unknown as "deny" would log every user out the moment the
        // menu endpoint hiccups.
        catchError(() => of([] as string[])),
        shareReplay(1)
      );
      this.routes$.subscribe(r => this.routesSubject.next(r));
    }
    return this.routes$;
  }

  /** Flatten the nested menu payload into normalised route strings. */
  private static flatten(rows: any[]): string[] {
    const out: string[] = [];
    const visit = (n: any) => {
      if (!n) { return; }
      const u = MenuScopeService.normalise(n.mnu_itm_url_tx);
      if (u) { out.push(u); }
      (n.children || []).forEach(visit);
      (n.submenu || []).forEach(visit);
    };
    (rows || []).forEach(visit);
    return Array.from(new Set(out));
  }

  get current(): string[] | null { return this.routesSubject.value; }

  /**
   * Is `url` inside the user's app profile?
   *
   * FAIL OPEN when the set is empty. That is deliberate and it is the difference
   * between a guard and an outage: 1,480 of 6,889 active usr_clnt_tnt_rel_t rows
   * (21%) carry NO app_prfle_id, so they resolve to an empty menu. Denying on an
   * empty set logs out a fifth of the user base on the first navigation — while
   * closing nothing, because the API is reachable without the browser either way.
   *
   * A prefix match is used, not equality: the profile grants '/internal/reports/list'
   * while the app routes to '/internal/reports/list/distance', and child routes of a
   * granted screen belong to that screen.
   */
  isPermitted(url: string): boolean {
    const routes = this.routesSubject.value;
    if (MenuScopeService.isEmpty(routes)) { return true; }   // unknown → allow

    const target = MenuScopeService.normalise(url);
    if (!target) { return true; }

    if (MenuScopeService.ALWAYS_ALLOWED.some(a => MenuScopeService.normalise(a) === target)) {
      return true;
    }
    return routes.some(r => target === r || target.indexOf(r + '/') === 0);
  }
}
